/**
 * Internal App Tracking System
 *
 * Tracks events WITH full user data for internal analytics
 * Sends events to backend API for storage and analysis
 */

import { config } from '../Environment';
import { isUserTester } from './analytics';

export interface AppEventData {
  [key: string]: any;
}

/**
 * Get tester metadata for analytics
 */
const getTesterMetadata = (): { is_tester: boolean; tester_mode: string; tester_expires_at?: string } => {
  if (typeof window === 'undefined') {
    return { is_tester: false, tester_mode: 'none' };
  }

  try {
    // User data is stored in a separate 'user' key, not under 'auth'
    const userData = localStorage.getItem('user');
    if (!userData) {
      return { is_tester: false, tester_mode: 'none' };
    }

    const user = JSON.parse(userData);
    const isTester = isUserTester();

    if (!isTester) {
      return { is_tester: false, tester_mode: 'none' };
    }

    // Include expiration if set
    const metadata: { is_tester: boolean; tester_mode: string; tester_expires_at?: string } = {
      is_tester: true,
      tester_mode: 'active',
    };

    if (user?.testerExpiresAt && user.testerExpiresAt !== null) {
      metadata.tester_expires_at = user.testerExpiresAt;
    }

    return metadata;
  } catch (error) {
    console.error('[AppTracking] Error getting tester metadata:', error);
    return { is_tester: false, tester_mode: 'none' };
  }
};

/**
 * Get the current user's email from localStorage auth data
 */
const getUserEmail = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const authData = localStorage.getItem('auth');
    if (!authData) return undefined;
    const auth = JSON.parse(authData);
    return auth?.email || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Track an app event with full user data
 * Automatically adds userEmail and tester metadata to all events
 * @param eventName - Name of the event (e.g., 'purchase_complete')
 * @param eventData - Event data including user information
 */
export const trackAppEvent = async (
  eventName: string,
  eventData: AppEventData = {}
): Promise<void> => {
  try {
    const testerMetadata = getTesterMetadata();
    const userEmail = getUserEmail();

    const payload = {
      eventName,
      eventData: {
        ...eventData,
        ...testerMetadata,
      },
      ...(userEmail ? { userEmail } : {}),
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    };

    console.log('[AppTracking] Event:', eventName, eventData, '| Tester:', testerMetadata);

    // Send to backend API
    const response = await fetch(`${config.API_BASE}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error('[AppTracking] Failed to send event:', response.statusText);
    }
  } catch (error) {
    console.error('[AppTracking] Error tracking event:', error);
    // Don't throw - tracking failures shouldn't break app functionality
  }
};

/**
 * Track purchase completion with full details
 */
export const trackPurchaseCompleteWithData = async (data: {
  paymentId: string;
  productName?: string;
  productType?: string;
  userEmail?: string;
  amount: number;
  currency: string;
  method?: string;
}): Promise<void> => {
  await trackAppEvent('purchase_complete', data);
};

/**
 * Track user registration with full details
 */
export const trackRegistrationWithData = async (data: {
  email: string;
  displayName: string;
}): Promise<void> => {
  await trackAppEvent('web_registration_success', data);
};

/**
 * Track contact event with user details
 */
export const trackContactWithData = async (data: {
  userEmail?: string;
  method: 'email' | 'calendly' | 'form';
}): Promise<void> => {
  await trackAppEvent('contact', data);
};

/**
 * Track redesign service registration with full form data
 */
export const trackRedesignRegistrationWithData = async (data: {
  workflowId: string;
  processId?: string;
  userData: Record<string, any>;
}): Promise<void> => {
  await trackAppEvent('redesign_registration_success', data);
};
